import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { GLItemKind, GLItemStatus } from '../../../DTO/GLItemDTO'
import { GLItemsDTO, SALES_PIPELINE_STATUSES, SalesChartLayout, SalesChartMode } from '../../../DTO/GLItemsDTO'
import mainservice from '../../../services/MainService'
import { MItem } from '../Menu'
import PlaningToolRoot from '../PlaningToolRoot'
import SalesPotentialChart, { SalesPotentialModeToggle } from './SalesPotentialChart'

const WON_STATUSES = [GLItemStatus.marketingOrderWon]
const LOST_STATUSES = [GLItemStatus.marketingOrderLost]

function statusFromNav(): GLItemStatus | undefined {
  const raw = `${mainservice.nav.getVal('status') || ''}`
  if (raw === 'all' || raw === '') {
    return undefined
  }
  const n = parseInt(raw, 10)
  if (Number.isNaN(n) || SALES_PIPELINE_STATUSES.indexOf(n) < 0) {
    return undefined
  }
  return n
}

function chartModeFromNav(): SalesChartMode {
  const raw = `${mainservice.nav.getVal('chartMode') || ''}`
  if (raw === 'project' || raw === 'compare') {
    return raw
  }
  return 'company'
}

function chartLayoutFromNav(): SalesChartLayout {
  const raw = `${mainservice.nav.getVal('chartLayout') || ''}`
  return raw === 'list' ? 'list' : 'graph'
}

export default function SalesPotentialPage() {
  const [dto] = useState(() => new GLItemsDTO({ kind: GLItemKind.marketing }))
  const [loading, setLoading] = useState(true)
  const [dataTick, setDataTick] = useState(0)
  const [mode, setMode] = useState<SalesChartMode>(() => chartModeFromNav())
  const [layout, setLayout] = useState<SalesChartLayout>(() => chartLayoutFromNav())
  const initialStatus = statusFromNav()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      await dto.getChartFromServer({ kind: GLItemKind.marketing })
      if (!cancelled) {
        setLoading(false)
        setDataTick(t => t + 1)
      }
    }
    load()
    return () => { cancelled = true }
  }, [dto])

  function changeMode(next: SalesChartMode) {
    setMode(next)
    mainservice.nav.setVal('chartMode', next)
  }

  function changeLayout(next: SalesChartLayout) {
    setLayout(next)
    mainservice.nav.setVal('chartLayout', next)
  }

  return (
    <PlaningToolRoot
      standalone
      menuItem={MItem.dashboardDemo}
      mainContentClass="SalesPotentialPage flex flex-column"
    >
      <div className="contentHeader">
        <h2>Gewichtetes Potenzial</h2>
      </div>
      <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SalesPotentialModeToggle mode={mode} onChange={changeMode} />
        {loading && <Box sx={{ color: '#666' }}>Lade…</Box>}
        {!loading && (
          <>
            <SalesPotentialChart
              mode={mode}
              variant="full"
              kind={GLItemKind.marketing}
              statuses={SALES_PIPELINE_STATUSES}
              initialStatus={initialStatus}
              groupBy="quarter"
              layout={layout}
              onLayoutChange={changeLayout}
              showTotalLine={mode !== 'compare'}
              dto={dto}
              dataTick={dataTick}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              <SalesPotentialChart
                title="Gewonnen"
                variant="tile"
                mode={mode}
                kind={GLItemKind.marketing}
                statuses={WON_STATUSES}
                groupBy="year"
                layout={layout}
                dto={dto}
                dataTick={dataTick}
              />
              <SalesPotentialChart
                title="Verloren"
                variant="tile"
                mode={mode}
                kind={GLItemKind.marketing}
                statuses={LOST_STATUSES}
                groupBy="year"
                layout={layout}
                dto={dto}
                dataTick={dataTick}
              />
              <SalesPotentialChart
                title="Frischetheke"
                variant="tile"
                mode={mode}
                kind={GLItemKind.marketing}
                statuses={SALES_PIPELINE_STATUSES}
                groupBy="freshness"
                layout={layout}
                dto={dto}
                dataTick={dataTick}
              />
            </Box>
          </>
        )}
      </Box>
    </PlaningToolRoot>
  )
}
